/** TipLine — one usage tip a day, as a slim line at the foot of Home's "This month" card (see tips.ts). */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lightbulb, X } from 'lucide-react';
import { Icon } from '../design/Icon';
import { openCommandPalette } from '../components/CommandPalette';
import { openShortcutsSheet } from '../components/ShortcutsSheet';
import { dismissTipForToday, tipForToday, turnTipsOff, type Tip, type TipAction } from './tips';

/** Today's tip, or null when there is none or it was dismissed / turned off in this visit. */
export function useTodayTip(email: string, driversEnabled: boolean) {
  const [hidden, setHidden] = useState(false);
  const tip = useMemo(
    () => tipForToday(email, {
      driversEnabled,
      wide: window.matchMedia?.('(min-width: 1280px)').matches ?? false,
      phone: window.matchMedia?.('(max-width: 767px)').matches ?? false,
    }),
    [email, driversEnabled],
  );
  return {
    tip: tip && !hidden ? tip : null,
    dismiss: () => { dismissTipForToday(email); setHidden(true); },
    turnOff: () => { turnTipsOff(email); setHidden(true); },
  };
}

export function TipLine({ tip, onDismiss, onTurnOff }: { tip: Tip; onDismiss: () => void; onTurnOff: () => void }) {
  const navigate = useNavigate();

  const run = (a: TipAction) => {
    if (a.kind === 'palette') openCommandPalette();
    else if (a.kind === 'shortcuts') openShortcutsSheet();
    else navigate(a.to);
  };

  return (
    <div role="note" className="relative flex items-start gap-2 pr-7" aria-label="Tip">
      <Icon as={Lightbulb} size={14} className="mt-0.5 shrink-0 text-warning" />
      <div className="min-w-0 flex-1 text-xs">
        <p className="font-medium text-fg">{tip.title}</p>
        <p className="mt-0.5 text-fg-muted">{tip.body}</p>
        <div className="mt-1 flex flex-wrap items-center gap-3">
          {tip.action && (
            <button type="button" className="text-brand hover:underline underline-offset-2" onClick={() => run(tip.action!.do)}>
              {tip.action.label}
            </button>
          )}
          <button
            type="button"
            className="text-fg-faint hover:text-fg underline underline-offset-2"
            onClick={onTurnOff}
          >
            Don&rsquo;t show tips
          </button>
        </div>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss tip"
        className="absolute -top-1 -right-1 inline-flex h-7 w-7 items-center justify-center rounded-md text-fg-faint hover:text-fg hover:bg-surface-2"
      >
        <Icon as={X} size={14} />
      </button>
    </div>
  );
}
